import React, { useEffect, useState } from 'react';
import { getConnectionAttempts, declareAttempt, ignoreAttempt } from '../../services/api';

const STATUS_LABELS = {
  PENDING: ['En attente', '#fff4e0', '#955d00'],
  DECLARED: ['Déclarée', '#e2f3e8', '#1a7f37'],
  IGNORED: ['Ignorée', '#ececec', '#6b7280'],
  RESOLVED: ['Résolue', '#e3edf9', '#005cc5'],
};

const emptyForm = {
  clientName: '', clientEmail: '', maxUsers: '', uniqueUserWindowDays: '30',
  validFrom: new Date().toISOString().slice(0, 10),
  validUntil: ''
};

/**
 * Serveurs dont la connexion (check-in / report) a été refusée : clé de licence
 * inconnue, désactivée ou expirée. « Déclarer » crée la licence avec la clé que
 * le serveur présente déjà — il s'auto-enregistre au check-in suivant.
 */
export default function ConnectionAttemptsPage() {
  const [attempts, setAttempts] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [declareModal, setDeclareModal] = useState(null); // tentative en cours de déclaration
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); // licence créée (confirmation)

  const load = all => getConnectionAttempts(all).then(setAttempts);
  useEffect(() => { load(showAll); }, [showAll]);

  const openDeclare = attempt => {
    setDeclareModal(attempt);
    setForm(emptyForm);
    setError('');
    setCreated(null);
  };

  const closeDeclare = () => {
    setDeclareModal(null);
    setCreated(null);
    load(showAll);
  };

  const handleDeclare = async e => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const license = await declareAttempt(declareModal.id, {
        ...form,
        maxUsers: parseInt(form.maxUsers),
        uniqueUserWindowDays: parseInt(form.uniqueUserWindowDays) || 30,
        validFrom: form.validFrom + 'T00:00:00',
        validUntil: form.validUntil + 'T23:59:59',
      });
      setCreated(license);
    } catch (err) {
      setError(err.response?.data?.error || 'Échec de la déclaration.');
    } finally {
      setSaving(false);
    }
  };

  const handleIgnore = async id => { await ignoreAttempt(id); load(showAll); };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h1 style={{ margin: 0 }}>Tentatives de connexion</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {[false, true].map(all => (
            <button key={String(all)} onClick={() => setShowAll(all)}
              style={{ ...filterBtn, ...(showAll === all ? { background: '#232853', color: '#fff', borderColor: '#232853' } : {}) }}>
              {all ? 'Toutes' : 'En attente'}
            </button>
          ))}
        </div>
      </div>
      <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>
        Serveurs refusés au check-in ou au report (clé de licence inconnue, désactivée ou expirée).
        « Déclarer » crée la licence avec la clé présentée : le serveur s'enregistre tout seul à sa
        prochaine connexion, sans reconfiguration.
      </p>

      <div style={{ background: '#ffffff', borderRadius: '12px', overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: '1080px', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#8a8f99', fontSize: '0.8rem', textTransform: 'uppercase', borderBottom: '1px solid #cdcdcd' }}>
              {['Serveur', 'IP', 'Clé présentée', 'Environnement', 'Version', 'Motif du refus', 'Tentatives', 'Dernière', 'Statut', 'Actions'].map(h =>
                <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left' }}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {attempts.map(a => {
              const [label, bg, color] = STATUS_LABELS[a.status] || [a.status, '#ececec', '#6b7280'];
              return (
                <tr key={a.id} style={{ borderBottom: '1px solid #ececec' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ ...td, fontWeight: 600 }}>{a.instanceId || <span style={muted}>non déclaré</span>}</td>
                  <td style={{ ...td, fontFamily: 'monospace', color: '#6b7280', fontSize: '0.85rem' }}>{a.callerIp}</td>
                  <td style={{ ...td, fontFamily: 'monospace', fontSize: '0.78rem', color: '#8a8f99' }}>
                    {a.presentedKey || <span style={muted}>aucune</span>}
                  </td>
                  <td style={td}>{a.environment || '—'}</td>
                  <td style={{ ...td, fontSize: '0.85rem', color: '#6b7280' }}>{a.digibrainVersion || '—'}</td>
                  <td style={{ ...td, fontSize: '0.85rem', color: '#b3261e' }}>{a.reason}</td>
                  <td style={{ ...td, fontWeight: 600 }}>{a.attemptCount}</td>
                  <td style={{ ...td, color: '#6b7280', fontSize: '0.82rem' }}>
                    {a.lastAttemptAt ? new Date(a.lastAttemptAt).toLocaleString('fr-FR') : '—'}
                  </td>
                  <td style={td}>
                    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: '99px', fontSize: '0.78rem', fontWeight: 600 }}>{label}</span>
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {a.status === 'PENDING' && (
                      <>
                        <button onClick={() => openDeclare(a)} disabled={!a.presentedKey}
                                title={a.presentedKey ? '' : 'Aucune clé présentée : configurer package-server.token sur l\'instance'}
                                style={{ ...okBtn, marginRight: '0.5rem', ...(a.presentedKey ? {} : { opacity: 0.5, cursor: 'not-allowed' }) }}>
                          Déclarer
                        </button>
                        <button onClick={() => handleIgnore(a.id)} style={cancelBtn}>Ignorer</button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {attempts.length === 0 && (
              <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#8a8f99', padding: '3rem' }}>
                {showAll ? 'Aucune tentative journalisée' : 'Aucune tentative en attente'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {declareModal && (
        <div style={overlay}>
          {created ? (
            <div style={modal}>
              <h3 style={{ margin: '0 0 1rem', color: '#1a7f37' }}>Licence créée ✔</h3>
              <p style={{ color: '#444444', fontSize: '0.9rem' }}>
                Licence <b>{created.clientName}</b> créée avec la clé
                {' '}<code style={{ fontSize: '0.8rem' }}>{created.licenseKey}</code>.<br />
                Le serveur <b>{declareModal.instanceId || declareModal.callerIp}</b> s'enregistrera
                automatiquement à son prochain check-in — rien d'autre à faire.
              </p>
              <div style={{ textAlign: 'right', marginTop: '1rem' }}>
                <button onClick={closeDeclare} style={primaryBtn}>Fermer</button>
              </div>
            </div>
          ) : (
            <form style={modal} onSubmit={handleDeclare}>
              <h3 style={{ margin: '0 0 0.5rem', color: '#232853' }}>Déclarer le serveur</h3>
              <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                Crée une licence avec la clé présentée par
                {' '}<b>{declareModal.instanceId || declareModal.callerIp}</b> :
                {' '}<code style={{ fontSize: '0.78rem' }}>{declareModal.presentedKey}</code>
              </p>
              <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: '1fr 1fr' }}>
                <Field label="Nom du client" value={form.clientName} onChange={v => setForm({ ...form, clientName: v })} required />
                <Field label="Email" type="email" value={form.clientEmail} onChange={v => setForm({ ...form, clientEmail: v })} required />
                <Field label="Quota utilisateurs" type="number" value={form.maxUsers} onChange={v => setForm({ ...form, maxUsers: v })} required />
                <Field label="Fenêtre users uniques (jours)" type="number" value={form.uniqueUserWindowDays} onChange={v => setForm({ ...form, uniqueUserWindowDays: v })} required />
                <Field label="Valide à partir du" type="date" value={form.validFrom} onChange={v => setForm({ ...form, validFrom: v })} required />
                <Field label="Valide jusqu'au" type="date" value={form.validUntil} onChange={v => setForm({ ...form, validUntil: v })} required />
              </div>
              {error && <div style={{ color: '#c62828', fontSize: '0.85rem', marginTop: '0.75rem' }}>{error}</div>}
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
                <button type="button" onClick={closeDeclare} style={cancelBtn}>Annuler</button>
                <button type="submit" disabled={saving} style={primaryBtn}>
                  {saving ? 'Création...' : 'Créer la licence'}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required }) {
  return (
    <div>
      <label style={{ display: 'block', color: '#6b7280', fontSize: '0.8rem', marginBottom: '4px' }}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
        style={{ width: '100%', padding: '0.6rem', background: '#f5f5f5', border: '1px solid #cdcdcd', color: '#444444', borderRadius: '6px', boxSizing: 'border-box' }}
      />
    </div>
  );
}

const td = { padding: '0.85rem 1rem', color: '#444444' };
const muted = { color: '#b0b5bf', fontStyle: 'italic' };
const filterBtn = { padding: '0.4rem 0.9rem', background: 'transparent', border: '1px solid #cdcdcd', color: '#6b7280', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' };
const okBtn = { padding: '0.35rem 0.85rem', background: '#e2f3e8', border: '1px solid #a7d7b4', color: '#1a7f37', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' };
const cancelBtn = { padding: '0.35rem 0.85rem', background: 'transparent', border: '1px solid #cdcdcd', color: '#6b7280', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' };
const primaryBtn = { padding: '0.5rem 1.25rem', background: '#232853', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 };
const modal = { background: '#ffffff', borderRadius: '12px', padding: '2rem', width: '560px', maxWidth: 'calc(100vw - 2rem)', boxSizing: 'border-box', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
